import { type QuantaHeaders, sha256Hex } from './auth';
import { quantaApi } from './quantaApi';

export type MigrationNetwork = 'mainnet' | 'testnet';
export interface MigrationWallet {
  address: string;
  publicKey: string;
  network: MigrationNetwork;
}
export interface MigrationAuthority {
  userId: string;
  revision: number;
  authority: MigrationWallet;
  signingAuthority?: MigrationWallet | null;
  canMigrate: boolean;
  migrated: boolean;
}
export interface MigrationContext {
  operation: 'ducat:quanta:metamask-migration:v2' | 'ducat:quanta:metamask-migration:v3' | 'ducat:quanta:metamask-migration:v4';
  targetUserId?: string;
  targetRevision?: number;
  origin: string;
  userId: string;
  revision: number;
  network: MigrationNetwork;
  source: MigrationWallet;
  target: MigrationWallet;
  nonce: string;
  expiresAt: string;
}
export interface MigrationChallenge {
  proofVersion: 2 | 3 | 4;
  nonce: string;
  expiresAt: string;
  context: MigrationContext;
  message: string;
  hash: string;
}
export interface MigrationStatus {
  status: 'pending' | 'completed' | 'expired' | 'canceled';
  userId?: string;
  revision?: number;
  authority?: MigrationWallet;
}
export interface MigrationRecovery {
  nonce: string;
  userId: string;
  source: MigrationWallet;
  target: MigrationWallet;
  expiresAt: string;
}

export function migrationContext(
  authority: MigrationAuthority,
  target: MigrationWallet,
  origin: string,
  nonce: string,
  expiresAt: string,
): MigrationContext {
  return {
    operation: 'ducat:quanta:metamask-migration:v2',
    origin,
    userId: authority.userId,
    revision: authority.revision,
    network: (authority.signingAuthority ?? authority.authority).network,
    source: authority.signingAuthority ?? authority.authority,
    target,
    nonce,
    expiresAt,
  };
}

export function sameMigrationWallet(left: MigrationWallet, right: MigrationWallet) {
  return left.address === right.address && left.publicKey.toLowerCase() === right.publicKey.toLowerCase() && left.network === right.network;
}

export async function fetchMigrationCapabilities() {
  const response = await quantaApi.get('/wallet-migration/capabilities');
  return response.data.data as {
    enabled: boolean;
    proofVersion: number;
    registrationWallet: string;
    mergeProofVersion?: number;
    crossNetworkProofVersion?: number;
  };
}
export async function fetchMigrationAuthority(headers: QuantaHeaders) {
  const response = await quantaApi.get('/wallet-migration/authority', { headers });
  return response.data.data as MigrationAuthority;
}
export async function fetchMigrationStatus(nonce: string, headers: QuantaHeaders) {
  const response = await quantaApi.post('/wallet-migration/status', { nonce }, { headers });
  return response.data.data as MigrationStatus;
}
export async function cancelMigration(nonce: string, headers: QuantaHeaders) {
  const response = await quantaApi.post('/wallet-migration/cancel', { nonce }, { headers });
  return response.data.data as MigrationStatus;
}

const recoveryKey = 'quanta-metamask-migration-v1';
// Persist a lookup handle only. Authentication proofs and signatures stay in memory.
export function saveMigrationRecovery(value: MigrationRecovery | null) {
  try {
    if (value)
      window.sessionStorage.setItem(
        recoveryKey,
        JSON.stringify({
          nonce: value.nonce,
          userId: value.userId,
          source: value.source,
          target: value.target,
          expiresAt: value.expiresAt,
        }),
      );
    else window.sessionStorage.removeItem(recoveryKey);
  } catch {
    // An in-memory flow still works when browser storage is disabled.
  }
}
export function readMigrationRecovery(): MigrationRecovery | null {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(recoveryKey) ?? 'null') as MigrationRecovery | null;
    if (
      parsed &&
      typeof parsed.nonce === 'string' &&
      /^[a-f0-9]{64}$/.test(parsed.nonce) &&
      typeof parsed.userId === 'string' &&
      Number.isFinite(Date.parse(parsed.expiresAt)) &&
      [parsed.source, parsed.target].every(
        (identity) =>
          identity &&
          typeof identity.address === 'string' &&
          typeof identity.publicKey === 'string' &&
          ['mainnet', 'testnet'].includes(identity.network),
      )
    )
      return parsed;
  } catch {
    // Invalid local metadata grants no permission and can be discarded.
  }
  return null;
}

export function migrationErrorCode(error: unknown): string | undefined {
  return (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
}

// The exact message builder is shared by the backend contract and its fixture.
export function validateWalletApprovalContext(context: Omit<MigrationContext, 'operation'>, crossNetwork = false): void {
  if (
    !Number.isSafeInteger(context.revision) ||
    context.revision < 0 ||
    context.revision > 2_147_483_646 ||
    !/^[A-Za-z0-9_-]{1,12}$/.test(context.userId) ||
    !['mainnet', 'testnet'].includes(context.network) ||
    !/^[a-f0-9]{64}$/.test(context.nonce) ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(context.expiresAt) ||
    !Number.isFinite(Date.parse(context.expiresAt)) ||
    new Date(context.expiresAt).toISOString() !== context.expiresAt ||
    !['mainnet', 'testnet'].includes(context.source.network) ||
    (crossNetwork ? context.source.network === context.target.network : context.source.network !== context.network) ||
    context.target.network !== context.network ||
    [
      context.origin,
      context.userId,
      context.source.address,
      context.source.publicKey,
      context.target.address,
      context.target.publicKey,
    ].some((value) => typeof value !== 'string' || /[\r\n]/.test(value))
  ) {
    throw new Error('Invalid migration approval context');
  }
}

export function migrationMessage(context: MigrationContext): string {
  const crossNetwork = context.operation === 'ducat:quanta:metamask-migration:v4';
  const combining =
    context.operation === 'ducat:quanta:metamask-migration:v3' ||
    (crossNetwork && (context.targetUserId !== undefined || context.targetRevision !== undefined));
  if (context.operation !== 'ducat:quanta:metamask-migration:v2' && !combining && !crossNetwork)
    throw new Error('Invalid migration approval context');
  validateWalletApprovalContext(context, crossNetwork);
  if (
    combining &&
    (!/^[A-Za-z0-9_-]{1,12}$/.test(context.targetUserId ?? '') ||
      context.targetUserId === context.userId ||
      !Number.isInteger(context.targetRevision) ||
      (context.targetRevision ?? -1) < 0 ||
      (context.targetRevision ?? Infinity) > 2_147_483_646)
  )
    throw new Error('Invalid account combination context');
  const message = [
    combining ? 'Combine Ducat Quanta accounts' : 'Ducat Quanta migration to MetaMask',
    combining
      ? 'Combine both earned balances. Target controls the account; source keeps login. No funds/vault transfer. Not login.'
      : 'Target controls this account; source keeps login. Points stay. No funds/vault transfer. Not login.',
    `Operation: ${context.operation}`,
    `Origin: ${context.origin}`,
    `Account: ${context.userId}`,
    `Revision: ${context.revision}`,
    ...(combining ? [`Target account: ${context.targetUserId}`, `Target revision: ${context.targetRevision}`] : []),
    ...(crossNetwork
      ? [`Source network: ${context.source.network}`, `Target network: ${context.target.network}`]
      : [`Network: ${context.network}`]),
    `Source: ${context.source.address}`,
    `Source key: ${context.source.publicKey}`,
    `Target: ${context.target.address}`,
    `Target key: ${context.target.publicKey}`,
    `Nonce: ${context.nonce}`,
    `Expires: ${context.expiresAt}`,
  ].join('\n');
  // The published Ducat Snap caps signing requests at 800 characters. A byte
  // limit is at least as strict and also protects clients with UTF-8 limits.
  if (new TextEncoder().encode(message).byteLength > 800) throw new Error('MIGRATION_MESSAGE_TOO_LONG');
  return message;
}

export async function requestMigrationChallenge(
  authority: MigrationAuthority,
  target: MigrationWallet,
  headers: QuantaHeaders,
  origin: string,
  allowAccountMerge = false,
  allowCrossNetwork = false,
) {
  const crossNetwork = (authority.signingAuthority ?? authority.authority).network !== target.network;
  if (crossNetwork && !allowCrossNetwork) throw new Error('MIGRATION_UNAVAILABLE');
  const response = await quantaApi.post(
    '/wallet-migration/challenge',
    {
      origin,
      target,
      provider: 'metamask',
      ...(allowAccountMerge ? { allowAccountMerge: true } : {}),
      ...(crossNetwork ? { allowCrossNetwork: true } : {}),
    },
    { headers },
  );
  const challenge = response.data.data as MigrationChallenge;
  if (
    (crossNetwork
      ? challenge?.proofVersion !== 4
      : challenge?.proofVersion !== 2 && !(allowAccountMerge && challenge?.proofVersion === 3)) ||
    typeof challenge.nonce !== 'string' ||
    !/^[a-f0-9]{64}$/.test(challenge.nonce) ||
    !Number.isFinite(Date.parse(challenge.expiresAt)) ||
    Date.parse(challenge.expiresAt) <= Date.now() ||
    Date.parse(challenge.expiresAt) > Date.now() + 600_000
  ) {
    throw new Error('Invalid migration challenge');
  }
  const expected = migrationContext(authority, target, origin, challenge.nonce, challenge.expiresAt);
  if (challenge.proofVersion === 4) {
    expected.operation = 'ducat:quanta:metamask-migration:v4';
    expected.network = target.network;
  }
  if (
    challenge.proofVersion === 3 ||
    (challenge.proofVersion === 4 && (challenge.context?.targetUserId !== undefined || challenge.context?.targetRevision !== undefined))
  ) {
    if (!allowAccountMerge) throw new Error('Invalid account combination context');
    if (challenge.proofVersion === 3) expected.operation = 'ducat:quanta:metamask-migration:v3';
    expected.targetUserId = challenge.context?.targetUserId;
    expected.targetRevision = challenge.context?.targetRevision;
  }
  const message = migrationMessage(expected);
  if (
    JSON.stringify(challenge.context) !== JSON.stringify(expected) ||
    challenge.message !== message ||
    challenge.hash !== (await sha256Hex(message))
  ) {
    throw new Error('Migration challenge does not match the selected account and wallets');
  }
  return challenge;
}

export async function confirmMigration(
  challenge: MigrationChallenge,
  sourceSignature: string,
  targetSignature: string,
  headers: QuantaHeaders,
) {
  if (Date.parse(challenge.expiresAt) <= Date.now()) throw new Error('Migration challenge expired');
  if (!sourceSignature || !targetSignature) throw new Error('Both wallets must approve this migration');
  const response = await quantaApi.post(
    '/wallet-migration/confirm',
    {
      origin: challenge.context.origin,
      nonce: challenge.nonce,
      sourceSignature,
      targetSignature,
    },
    { headers },
  );
  return response.data.data as MigrationStatus;
}
